import type { ProjectGraphNode } from '@avisoma/shared';
import { Button } from '../../catalyst/button';
import { Field, Label } from '../../catalyst/fieldset';
import { Select } from '../../catalyst/select';
import { nodeLabel } from './graphModel';

// The value of the disabled option that stands for a focus the engine list cannot name.
const OTHER_FOCUS = '__focused__';

type Props = {
  engines: ProjectGraphNode[];
  entryNodeId: string | null;
  rootId: string | null;
  rootLabel: string;
  onChange: (nodeId: string | null) => void;
};

const byLabelThenId = (a: ProjectGraphNode, b: ProjectGraphNode) =>
  nodeLabel(a).localeCompare(nodeLabel(b)) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** Picks the graph's focus: the whole project or one engine; a document or part focus shows as a fixed entry. */
export function FocusPicker({ engines, entryNodeId, rootId, rootLabel, onChange }: Props) {
  if (engines.length === 0) {
    return (
      <div className="flex items-center justify-between gap-2 text-sm text-zinc-900 dark:text-zinc-100">
        <span className="min-w-0 truncate">
          Focused on <span className="font-medium">{rootLabel}</span>
        </span>
        {rootId !== null && (
          <Button plain className="shrink-0 whitespace-nowrap" onClick={() => onChange(null)}>
            Clear focus
          </Button>
        )}
      </div>
    );
  }

  const entry = engines.find((n) => n.id === entryNodeId);
  const ordered = [...(entry ? [entry] : []), ...engines.filter((n) => n !== entry).sort(byLabelThenId)];
  const otherFocus = rootId !== null && !engines.some((n) => n.id === rootId);
  const value = rootId === null ? '' : otherFocus ? OTHER_FOCUS : rootId;

  return (
    <Field>
      <Label>Focus</Label>
      <Select value={value} onChange={(e) => onChange(e.target.value || null)}>
        {otherFocus && (
          <option value={OTHER_FOCUS} disabled>
            {`Focused: ${rootLabel}`}
          </option>
        )}
        <option value="">Whole project</option>
        {ordered.map((n) => (
          <option key={n.id} value={n.id}>
            {n === entry ? `${nodeLabel(n)} · working` : nodeLabel(n)}
          </option>
        ))}
      </Select>
    </Field>
  );
}
